import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCountry } from '../services/api.ts';
import { ArrowLeft, Globe } from 'lucide-react';
import './AddCountry.css';

const AddCountry: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    enable: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);
      await createCountry(formData);
      navigate('/dashboard/countries');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to create country';
      setError(errorMessage);
      console.error('Create error:', err.response?.data);
    }
  };

  return (
    <div className="add-country">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/countries')}>
          <ArrowLeft size={20} />
          Back to Countries
        </button>
        <h2>Add New Country</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Country Information</h3>
            
            <div className="form-group">
              <label>Country Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter country name"
                required
              />
            </div>

            <div className="form-group">
              <label>Country Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Enter country code (e.g., US, CA, GB)"
                required
              />
              <p className="field-hint">ISO 3166-1 alpha-2 code for the country</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this country for selection</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon">
                  <Globe size={24} />
                </div>
                <span className={`preview-status ${formData.enable ? 'active' : 'inactive'}`}></span>
              </div>
              
              <div className="preview-content">
                <div className="preview-info">
                  <h4>{formData.name || 'Country Name'}</h4>
                  <p className="preview-code">{formData.code || 'CODE'}</p>
                </div>

                <div className="preview-badges">
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>🌍 Country Guidelines</h4>
              <ul>
                <li>Use official country names</li>
                <li>Country codes should follow ISO 3166-1 alpha-2 standard</li>
                <li>Keep codes uppercase for consistency</li>
                <li>Verify the code doesn't already exist</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/countries')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Create Country
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCountry;
